import React, { useState } from 'react';
import {
  QuestionMarkCircleIcon,
  XMarkIcon,
  BookOpenIcon,
  VideoCameraIcon,
  ChatBubbleLeftRightIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';

const helpTopics = {
  dashboard: {
    title: 'Dashboard',
    description: 'Your central hub for tracking your child\'s development',
    articles: [
      {
        title: 'Getting Started with the Dashboard',
        content: 'The dashboard provides a quick overview of your child\'s recent activities, upcoming reminders, and daily tips. Use the Quick Add buttons to log events quickly.',
      },
      {
        title: 'Understanding Dashboard Stats',
        content: 'Dashboard stats show summaries of feeding, sleep, and diaper changes. Click on any stat to view detailed history.',
      },
    ],
  },
  tracking: {
    title: 'Event Tracking',
    description: 'Log daily activities and milestones',
    articles: [
      {
        title: 'Logging Events',
        content: 'Use Quick Add buttons or the Events page to log feeding, sleep, diaper changes, and custom events. All events are timestamped automatically.',
      },
      {
        title: 'Editing Past Events',
        content: 'Click on any event in the Recent Activity list to edit or delete it. Changes are saved immediately.',
      },
    ],
  },
  sharing: {
    title: 'Family Sharing',
    description: 'Collaborate with family members',
    articles: [
      {
        title: 'Inviting Family Members',
        content: 'Go to the Sharing page and click "Invite User". Enter their email and select permission level (view-only or full access). They\'ll receive an invitation email.',
      },
      {
        title: 'Managing Permissions',
        content: 'You can change permissions or revoke access at any time from the Sharing page. All changes are logged in the audit trail.',
      },
    ],
  },
  content: {
    title: 'Content Hub',
    description: 'Educational articles and tips',
    articles: [
      {
        title: 'Finding Relevant Content',
        content: 'Content is personalized based on your child\'s age. Use the search feature to find specific topics or browse by category.',
      },
      {
        title: 'Saving Favorite Articles',
        content: 'Click the bookmark icon on any article to save it for later. Access saved articles from your profile.',
      },
    ],
  },
  reports: {
    title: 'Reports & Analytics',
    description: 'Generate comprehensive reports',
    articles: [
      {
        title: 'Generating Reports',
        content: 'Choose from predefined templates or create custom reports. Select date range, data types, and export format (PDF, CSV, or Excel).',
      },
      {
        title: 'Scheduling Automated Reports',
        content: 'Set up recurring reports to be generated automatically. Choose frequency (daily, weekly, monthly) and delivery method.',
      },
    ],
  },
  analytics: {
    title: 'Analytics & Insights',
    description: 'AI-powered insights and predictions',
    articles: [
      {
        title: 'Understanding Insights',
        content: 'The system analyzes patterns in your data to provide personalized insights about sleep, feeding, and growth trends.',
      },
      {
        title: 'Acting on Alerts',
        content: 'When the system detects concerning patterns, you\'ll receive alerts with recommended actions. Always consult your healthcare provider for medical concerns.',
      },
    ],
  },
};

const HelpCenter = ({ defaultTopic = null, onClose = null }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedTopic, setSelectedTopic] = useState(defaultTopic);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedArticle, setExpandedArticle] = useState(null);

  const handleOpen = () => setIsOpen(true);
  const handleClose = () => {
    setIsOpen(false);
    if (onClose) onClose();
  };

  const filteredTopics = Object.entries(helpTopics).filter(([, topic]) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      topic.title.toLowerCase().includes(query) ||
      topic.description.toLowerCase().includes(query) ||
      topic.articles.some(
        (article) =>
          article.title.toLowerCase().includes(query) ||
          article.content.toLowerCase().includes(query)
      )
    );
  });

  const currentTopic = selectedTopic ? helpTopics[selectedTopic] : null;

  if (!isOpen && !defaultTopic) {
    return (
      <button
        onClick={handleOpen}
        className="fixed bottom-6 right-6 z-50 flex items-center justify-center w-14 h-14 bg-blue-600 text-white rounded-full shadow-lg hover:bg-blue-700 transition-all duration-200 hover:scale-110"
        aria-label="Open Help Center"
      >
        <QuestionMarkCircleIcon className="h-7 w-7" />
      </button>
    );
  }

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={handleClose} />
      
      <div className="absolute right-0 top-0 h-full w-full max-w-2xl bg-card-bg shadow-xl">
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between p-6 border-b border-card-border bg-gradient-to-r from-blue-600 to-purple-600">
            <div className="flex items-center space-x-3">
              <QuestionMarkCircleIcon className="h-8 w-8 text-white" />
              <div>
                <h2 className="text-2xl font-bold text-white">Help Center</h2>
                <p className="text-blue-100 text-sm">Find answers and learn how to use features</p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="text-white hover:text-blue-100 transition-colors"
              aria-label="Close Help Center"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          <div className="p-6 border-b border-card-border">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-text-tertiary" />
              <input
                type="text"
                placeholder="Search help articles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-input-border bg-input-bg text-text-primary rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-6">
            {!currentTopic ? (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  <a
                    href="#"
                    className="flex items-center space-x-3 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors"
                  >
                    <BookOpenIcon className="h-6 w-6 text-blue-600" />
                    <div>
                      <h3 className="font-medium text-text-primary">Documentation</h3>
                      <p className="text-sm text-text-secondary">Complete guides</p>
                    </div>
                  </a>
                  
                  <a
                    href="#"
                    className="flex items-center space-x-3 p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg hover:bg-purple-100 dark:hover:bg-purple-900/30 transition-colors"
                  >
                    <VideoCameraIcon className="h-6 w-6 text-purple-600" />
                    <div>
                      <h3 className="font-medium text-text-primary">Video Tutorials</h3>
                      <p className="text-sm text-text-secondary">Watch and learn</p>
                    </div>
                  </a>
                </div>

                <h3 className="text-lg font-semibold text-text-primary mb-4">Browse Topics</h3>
                
                <div className="space-y-3">
                  {filteredTopics.map(([key, topic]) => (
                    <button
                      key={key}
                      onClick={() => setSelectedTopic(key)}
                      className="w-full text-left p-4 bg-bg-tertiary rounded-lg hover:bg-card-border transition-colors border border-card-border"
                    >
                      <h4 className="font-medium text-text-primary mb-1">{topic.title}</h4>
                      <p className="text-sm text-text-secondary">{topic.description}</p>
                      <p className="text-xs text-blue-600 mt-2">
                        {topic.articles.length} article{topic.articles.length !== 1 ? 's' : ''}
                      </p>
                    </button>
                  ))}
                </div>

                {filteredTopics.length === 0 && (
                  <div className="text-center py-8">
                    <p className="text-text-secondary">No help articles found for &quot;{searchQuery}&quot;</p>
                    <button
                      onClick={() => setSearchQuery('')}
                      className="mt-2 text-blue-600 hover:text-blue-700"
                    >
                      Clear search
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div>
                <button
                  onClick={() => setSelectedTopic(null)}
                  className="flex items-center text-blue-600 hover:text-blue-700 mb-4"
                >
                  <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  Back to topics
                </button>

                <h3 className="text-2xl font-bold text-text-primary mb-2">{currentTopic.title}</h3>
                <p className="text-text-secondary mb-6">{currentTopic.description}</p>

                <div className="space-y-4">
                  {currentTopic.articles.map((article, index) => (
                    <div key={index} className="border border-card-border rounded-lg overflow-hidden">
                      <button
                        onClick={() => setExpandedArticle(expandedArticle === index ? null : index)}
                        className="w-full text-left p-4 bg-bg-tertiary hover:bg-card-border transition-colors flex items-center justify-between"
                      >
                        <h4 className="font-medium text-text-primary">{article.title}</h4>
                        <svg
                          className={`w-5 h-5 text-text-tertiary transition-transform ${
                            expandedArticle === index ? 'transform rotate-180' : ''
                          }`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                      
                      {expandedArticle === index && (
                        <div className="p-4 bg-card-bg">
                          <p className="text-text-secondary leading-relaxed">{article.content}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="p-6 border-t border-card-border bg-bg-tertiary">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2 text-sm text-text-secondary">
                <ChatBubbleLeftRightIcon className="h-5 w-5" />
                <span>Need more help?</span>
              </div>
              <a
                href="mailto:support@childtracker.com"
                className="text-blue-600 hover:text-blue-700 font-medium text-sm"
              >
                Contact Support
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HelpCenter;
